/**
 * Declarative map of CCA / EIA variants.
 *
 * Add/adjust host+port rules here — no code elsewhere needs to change. Each
 * variant carries presentation metadata (label, product name, theme class, home
 * route) plus the matching rules used to resolve the mode from the URL.
 *
 * Matching rule fields (all optional). A variant matches if ANY of its rules
 * matches; when several disagree, the most TRUSTWORTHY signal wins:
 *
 *   hostname > port > path prefix
 *
 * Hostname and port describe the server you are actually talking to. A path is
 * just something in the address bar — typed, bookmarked, or left over from the
 * other product — so it only decides when neither of the others identifies a
 * variant. Otherwise `localhost:3001/eia/...` would make a CCA dev server serve
 * EIA.
 *
 * A DEPLOYED HOST IS RECOGNISED BY ITS NAME. Legacy decides the product by
 * whether the hostname contains "cca" or "eia" (utils/urlUtils.js), so every
 * environment it is deployed on — cca-prod.amd.com, eia-prod.amd.com, dev and
 * stage hosts — is named that way; `hostIncludes` is that same rule, so those
 * hosts need no list here. `hosts` (exact names) is checked first, for a host
 * whose name does not say which product it is. The /cca | /eia URL prefix is
 * local-only (router/basename.js): on any other host it is stripped from the
 * address bar on load. So a deployment cannot be told apart by path — one that
 * tried (both products under one origin) would pick the right product on the
 * first load, lose the prefix, and fall back to the default on the next. Off
 * localhost, a host matching nothing resolves to EIA — legacy's
 * `isCCA() ? CCA : EIA` — with a console warning (appMode.js).
 *
 * Requiring all three to agree would instead make local dev unmatchable, since
 * `localhost:3001` has no production hostname.
 *   - hosts: array of exact hostnames OR suffixes (startsWith '.' = suffix match)
 *   - hostIncludes: array of strings; the hostname containing one matches
 *     (legacy's rule). CCA's are tried before EIA's, as legacy's isCCA() runs
 *     first.
 *   - ports: array of port strings (the local-dev signal — see `pnpm dev:cca` / `dev:eia`)
 *   - pathPrefixes: array of URL path prefixes (e.g. '/eia')
 *
 * `devPorts` is the ordered candidate list `scripts/dev-variant.mjs` walks to
 * pick a free port, and is the single source of truth for it — so adding a dev
 * port here is all it takes. Every devPort must also appear in `match.ports`,
 * or the dev server would come up on a port the resolver doesn't recognize.
 */
export const VARIANTS = Object.freeze({
  cca: Object.freeze({
    mode: 'cca',
    productName: 'Enterprise Cloud Cost Advisor',
    // Browser tab title. Separate from productName because it carries the AMD
    // prefix the in-app header does not, and is applied at runtime by App.jsx —
    // index.html's static <title> cannot know the variant.
    documentTitle: 'AMD Enterprise Cloud Cost Advisor',
    shortName: 'CCA',
    // One-line product pitch for the Advisory landing page. Legacy Home.vue
    // picks between these with an inline isCCA(); here it is data, so a page
    // never branches on the product to choose its own copy.
    tagline:
      'Get real-time insights into estimated cost savings when switching to cloud ' +
      'instances powered by AMD within the same CSP.',
    themeClass: 'theme-cca',
    homeRoute: '/manage-portfolio',
    devPorts: Object.freeze(['3001', '3002', '3003']),
    match: {
      hosts: ['cca.epycadvisory.amd.com', '.cca.amd.com'],
      // Legacy isCCA(): the name contains "cca". Legacy also matched two CCA
      // servers reached by IP address; those are NOT listed here (a hard-coded
      // address failed the Jenkins security scan) — a deployment that needs
      // them sets VITE_CCA_EXTRA_HOSTS (config/env.js), passed to
      // matchVariantByLocation by appMode.js.
      hostIncludes: ['cca'],
      ports: ['3001', '3002', '3003'],
      pathPrefixes: ['/cca'],
    },
  }),
  eia: Object.freeze({
    mode: 'eia',
    // EIA's name (user order 2026-10-28): it was "Enterprise Cloud Instance
    // Advisor". The app bar, tab title, Home / login headings and the Support
    // page's sentences all read it from here.
    productName: 'Enterprise Instance Advisory',
    documentTitle: 'AMD Enterprise Instance Advisory',
    shortName: 'EIA',
    tagline:
      'Receive recommended instance types, sizes, and savings based on resource ' +
      'utilization statistics.',
    themeClass: 'theme-eia',
    homeRoute: '/manage-portfolio',
    devPorts: Object.freeze(['2001', '2002', '2003']),
    match: {
      hosts: ['eia.epycadvisory.amd.com', '.eia.amd.com'],
      // Legacy isEIA(): the name contains "eia".
      hostIncludes: ['eia'],
      ports: ['2001', '2002', '2003'],
      pathPrefixes: ['/eia'],
    },
  }),
});

function hostMatches(hostname, rules = []) {
  return rules.some((rule) =>
    rule.startsWith('.')
      ? hostname.endsWith(rule) || hostname === rule.slice(1)
      : hostname === rule,
  );
}

/**
 * Resolve a variant mode from a location-like object, or null if nothing matches.
 * @param {{hostname?:string, port?:string, pathname?:string}} location
 * @param {{ cca?: string[], eia?: string[] }} [extraHostIncludes]  deploy-time
 *   additions to each variant's `hostIncludes` (lower-case), by mode. Kept out of
 *   this file's data so no address needs to be written in source.
 * @returns {'cca' | 'eia' | null}
 */
export function matchVariantByLocation(location, extraHostIncludes = {}) {
  const hostname = location.hostname ?? '';
  const port = location.port ?? '';
  const pathname = location.pathname ?? '';

  const variants = Object.values(VARIANTS);

  // Most trustworthy signal first: the hostname, then the port, then the path.
  // Each tier is checked across ALL variants before falling through, so a weak
  // signal on one variant never outranks a strong signal on another.
  const byHost = variants.find((v) => hostMatches(hostname, v.match.hosts ?? []));
  if (byHost) return byHost.mode;

  const host = hostname.toLowerCase();
  const byHostName = variants.find((v) =>
    [...(v.match.hostIncludes ?? []), ...(extraHostIncludes?.[v.mode] ?? [])].some((s) =>
      host.includes(s),
    ),
  );
  if (byHostName) return byHostName.mode;

  const byPort = variants.find((v) => v.match.ports?.includes(port));
  if (byPort) return byPort.mode;

  // Last resort: the path only decides when the host and port say nothing.
  const byPath = variants.find((v) => v.match.pathPrefixes?.some((p) => pathname.startsWith(p)));
  if (byPath) return byPath.mode;

  return null;
}
